import { ProductInventoryGrid, Tabs, TabsContent, TabsList, TabsTrigger } from "randmarcomps";
import React from "react";
import { Product, WarehouseInventory } from "../../stores/product/Product";
import OpenOrdersGrid from "../grids/OpenOrdersGrid";
import OpenPurchaseOrdersGrid from "../grids/OpenPurchaseOrdersGrid";
import OpportunityDetailsGrid from "../grids/OpportunityDetailsGrid";
import ProductGeneral from "./ProductGeneral";

import withManufacturer from "../../contexts/WithManufacturer";

interface IProps {
  applicationId: string;
  product: Product;
  defaultTabIndex?: number;
}

const tabConfigs = [
  { value: "general", label: "General" },
  { value: "inventory", label: "Inventory" },
  { value: "purchases", label: "Purchases" },
  { value: "open-orders", label: "Open Orders" },
  { value: "completed-orders", label: "Completed Orders" },
  { value: "opportunities", label: "Opportunities" },
];

const ProductDescriptionTable: React.FC<IProps> = ({ product, defaultTabIndex }) => {
  const [activeTab, setActiveTab] = React.useState(tabConfigs[defaultTabIndex || 0].value);

  const sortedPurchaseOrders = React.useMemo(
    () =>
      (product.purchaseOrdersPartNumbers ?? [])
        .slice()
        .sort((a, b) => (a.purchaseOrderNumber || "").localeCompare(b.purchaseOrderNumber || "")),
    [product.purchaseOrdersPartNumbers]
  );

  const sortedRequisitions = React.useMemo(
    () =>
      (product.requisitionsPartNumbers ?? [])
        .slice()
        .sort((a, b) => (a.requisitionNumber || "").localeCompare(b.requisitionNumber || "")),
    [product.requisitionsPartNumbers]
  );

  const hasPurchases = Boolean((product.purchaseOrdersPartNumbers?.length ?? 0) > 0 || (product.requisitionsPartNumbers?.length ?? 0) > 0);
  const hasOpenOrders = Boolean((product.activeOrderDetails?.length ?? 0) > 0);
  const hasCompletedOrders = Boolean((product.completedOrderDetails?.length ?? 0) > 0);
  const hasOpportunities = Boolean((product.opportunities?.length ?? 0) > 0);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <div className="overflow-x-auto bg-slate-50 rounded-lg p-1">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="purchases" disabled={!hasPurchases}>Purchases</TabsTrigger>
          <TabsTrigger value="open-orders" disabled={!hasOpenOrders}>Open Orders</TabsTrigger>
          <TabsTrigger value="completed-orders" disabled={!hasCompletedOrders}>Completed Orders</TabsTrigger>
          <TabsTrigger value="opportunities" disabled={!hasOpportunities}>Opportunities</TabsTrigger>
        </TabsList>
      </div>

      <div className="mt-2.5">
        <TabsContent value="general">
          <ProductGeneral product={product} />
        </TabsContent>
        <TabsContent value="inventory">
          {
            product.distribution?.Inventory &&
            <ProductInventoryGrid inventory={product.distribution.Inventory.map((wi: WarehouseInventory) => ({
              Name: wi.Name ?? "",
              WarehouseId: wi.WarehouseId ?? "",
              AvailableQuantity: wi.AvailableQuantity ?? 0,
              Province: wi.Province ?? "",
              Country: wi.Country ?? "",
              PurchaseOrderQuantity: wi.PurchaseOrderQuantity ?? 0,
              BinLocation: wi.BinLocation ?? "",
            }))} />
          }
        </TabsContent>
        <TabsContent value="purchases">
          <OpenPurchaseOrdersGrid purchaseOrdersPartNumbers={sortedPurchaseOrders} requisitionsPartNumbers={sortedRequisitions} />
        </TabsContent>
        <TabsContent value="open-orders">
          <OpenOrdersGrid productOrders={product.activeOrderDetails} />
        </TabsContent>
        <TabsContent value="completed-orders">
          <OpenOrdersGrid productOrders={product.completedOrderDetails} defaultRowsPerPage={10} withSort />
        </TabsContent>
        <TabsContent value="opportunities">
          <OpportunityDetailsGrid productOpportunities={product.opportunities} product={product} />
        </TabsContent>
      </div>
    </Tabs>
  );
}

export default withManufacturer(ProductDescriptionTable);
